/**
 * +edge: the zone's natural edge (docs/design/zones.md "The edge"): past the walkable boundary the
 * ground rises into steep mountain flanks over most of the circuit, with stretches of forested
 * hills between them, and a low pass toward the Tower. Shared by the layout (terrain heights,
 * dressing) and the far world (atmosphere/farWorld.ts: its foothills keep climbing from here), so
 * the two meet without a seam. Pure functions of sim (x, y); presentation only.
 */

function hash(x: number, y: number): number {
  const s = Math.sin(x * 127.1 + y * 311.7) * 43758.5453;
  return s - Math.floor(s);
}
function vn(x: number, y: number): number {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const xf = x - xi;
  const yf = y - yi;
  const u = xf * xf * (3 - 2 * xf);
  const v = yf * yf * (3 - 2 * yf);
  const a = hash(xi, yi);
  const b = hash(xi + 1, yi);
  const c = hash(xi, yi + 1);
  const d = hash(xi + 1, yi + 1);
  return a + (b - a) * u + (c - a) * v + (a - b - c + d) * u * v;
}
const sm = (e0: number, e1: number, x: number): number => {
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
};

export interface EdgeFrame {
  /** Map centre (sim) and the direction toward the Tower (radians, sim), or null. */
  cx: number;
  cy: number;
  towerAng: number | null;
}

/** 0 = a forested stretch of the edge … 1 = mountains (by position: long stretches of each). */
export function mountainK(x: number, y: number): number {
  return sm(0.3, 0.44, vn(x * 0.0068 + 17.3, y * 0.0068 - 9.1));
}

/** 0 … 1 toward the Tower's direction from the map centre (the high pass is lower there). */
export function passK(f: EdgeFrame, x: number, y: number): number {
  if (f.towerAng === null) return 0;
  const a = Math.atan2(y - f.cy, x - f.cx);
  const da = Math.atan2(Math.sin(a - f.towerAng), Math.cos(a - f.towerAng));
  return Math.exp(-((da / 0.17) ** 2));
}

/**
 * Height (yd) the edge adds at distance `e` yd outside the walkable boundary (e ≤ 0: inside, a
 * gentle foothill lift in mountain stretches). Mountains: a steep lip, then ridged flanks climbing
 * ~60–80 yd by the terrain's margin; forest: rolling hills ~20 yd.
 */
export function edgeLift(f: EdgeFrame, x: number, y: number, e: number): number {
  const mk = mountainK(x, y);
  const pass = passK(f, x, y);
  // At the boundary itself both sides agree (no step): the inside lift's top is the outside's foot.
  const l0 = 4.5 * mk * (1 - pass);
  if (e <= 0) return l0 * sm(-30, 0, e);
  const ridged = 1 - Math.abs(vn(x * 0.021 + 5, y * 0.021 - 3) * 2 - 1);
  const crag = vn(x * 0.07 + 2, y * 0.07 + 8);
  const mountain = 8 * sm(0, 9, e) + 68 * Math.pow(sm(2, 95, e), 0.85) * (0.55 + 0.65 * ridged) + 6 * (crag - 0.5) * sm(4, 20, e);
  const roll = 0.62 + 0.38 * vn(x * 0.045 + 3, y * 0.045 - 7);
  const forest = 2.5 * sm(0, 10, e) + 20 * sm(4, 70, e) * roll;
  return l0 + (forest + (mountain - forest) * mk) * (1 - 0.6 * pass * sm(10, 60, e));
}
